import { EndpointProductService } from 'common/services/EndpointProductService';
import { useAppDispatch } from 'features/app/hooks';
import { SoundName } from 'features/audio-player/enums';
import { AudioPlayer } from 'features/audio-player/services';
import { CallType } from 'features/call/call-base/enums';
import { useIncomingCall } from 'features/call/call-base/hooks';
import {
  handleResetCall,
  setCallType,
  handleReceivedCall,
} from 'features/call/call-base/store';
import { setReceivedDeaf } from 'features/call/call-deaf/store';
import { primaryHearingReceived } from 'features/call/call-hearing/store';
import { callReceived } from 'features/call/call-ui-state/store';
import { incomingCallPushed } from 'features/caller-session/store';
import { ColdHandoffDaoService } from 'features/cold-handoff/services';
import {
  coldHandoffRequestReceived,
  coldHandoffAcceptedByUser,
  coldHandoffCanceledByAcceptingUser,
} from 'features/cold-handoff/store';
import { handleError } from 'features/notification/store';
import type {
  ColdHandoffAccepted,
  ColdHandoffCanceled,
  ColdHandoffRequest,
} from 'features/rns/interfaces';
import { enableSpawnCall } from 'features/spawn-call/store';
import { setIsVcoRequested } from 'features/vco/store';
import { setVoiceSessionId } from 'features/voice-session/store';
import { enableVriChat, resetVriChat } from 'features/vri-chat/store';

export const useColdHandoffRnsHandlers = () => {
  const dispatch = useAppDispatch();
  const { playIncomingCallSound } = useIncomingCall();

  const handleColdHandoffRequest = (data: ColdHandoffRequest) => {
    dispatch(handleReceivedCall(data.HandoffCall));
    dispatch(callReceived());
    dispatch(setReceivedDeaf(data.HandoffCall));
    dispatch(primaryHearingReceived(data.HandoffCall));
    dispatch(setVoiceSessionId(data.HandoffCall.AudioCallUrl));
    dispatch(setIsVcoRequested(data.HandoffCall.IsVcoRequested));
    dispatch(incomingCallPushed(data));
    dispatch(
      coldHandoffRequestReceived({
        requestingUserName: data.HandoffAgent.FullName,
      })
    );

    EndpointProductService.setName(data.HandoffCall.EndpointProductName);

    if (
      EndpointProductService.isSignTimeWeb ||
      EndpointProductService.isSignTimeIos
    ) {
      dispatch(enableVriChat());
    }

    // only signTime iOS needs the ability to spawn new calls until further notice
    if (EndpointProductService.isSignTimeIos) {
      dispatch(enableSpawnCall());
    }

    dispatch(setCallType(CallType.COLD_HANDOFF_CALL));
    playIncomingCallSound();
  };

  const handleColdHandoffAccepted = (data: ColdHandoffAccepted) => {
    dispatch(
      coldHandoffAcceptedByUser({
        acceptingUserUri: `${data.HandoffAgentVpPhoneNumber}@${data.AgentDNS}`,
      })
    );
    // TODO: not covered with tests
    AudioPlayer.play(SoundName.ACCEPTED);
  };

  const handleColdHandoffCanceledRequestingUser = async (
    data: ColdHandoffCanceled
  ) => {
    try {
      await ColdHandoffDaoService.restoreHandoff({
        callId: data.Call.Id,
      });

      dispatch(coldHandoffCanceledByAcceptingUser());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleColdHandoffCanceledRequestingUser',
        })
      );
    }
  };

  const handleColdHandoffCancelCallReceived = () => {
    AudioPlayer.stop();
    dispatch(handleResetCall()).unwrap();
    dispatch(resetVriChat());
  };

  return {
    handleColdHandoffRequest,
    handleColdHandoffAccepted,
    handleColdHandoffCanceledRequestingUser,
    handleColdHandoffCancelCallReceived,
  };
};
